import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Link } from 'react-router-dom';
import { TreePine, Users, Award, Clock, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import ErrorBoundary from '../ErrorBoundary';
import SkeletonLoader from '../SkeletonLoader';
import ImageWithFallback from '../ImageWithFallback';

interface DashboardStats {
  totalTrees: number;
  pendingTrees: number;
  approvedTrees: number;
  totalVotes: number;
  recentTrees: any[];
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'approved':
      return { label: 'Aprobado', classes: 'bg-green-100 text-green-800' };
    case 'rejected':
      return { label: 'Rechazado', classes: 'bg-red-100 text-red-800' };
    case 'deleted':
      return { label: 'En papelera', classes: 'bg-gray-100 text-gray-800' };
    default:
      return { label: 'Pendiente', classes: 'bg-yellow-100 text-yellow-800' };
  }
};

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const treesRef = collection(db, 'trees');
        
        // Get all trees
        const allTreesSnapshot = await getDocs(treesRef);
        const allTrees = allTreesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Get pending trees
        const pendingQuery = query(treesRef, where('status', '==', 'pending'));
        const pendingSnapshot = await getDocs(pendingQuery);

        // Get approved trees
        const approvedQuery = query(treesRef, where('status', '==', 'approved'));
        const approvedSnapshot = await getDocs(approvedQuery);

        // Get recent trees
        const recentQuery = query(
          treesRef,
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const recentSnapshot = await getDocs(recentQuery);
        const recentTrees = recentSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));

        // Calculate total votes
        const totalVotes = allTrees.reduce((sum, tree) => sum + (tree.votes || 0), 0);

        setStats({
          totalTrees: allTreesSnapshot.size,
          pendingTrees: pendingSnapshot.size,
          approvedTrees: approvedSnapshot.size,
          totalVotes,
          recentTrees
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Error al cargar los datos del dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
              <div className="flex justify-between">
                <div className="space-y-3">
                  <SkeletonLoader variant="text" className="w-24" />
                  <SkeletonLoader variant="text" className="w-16 h-8" />
                  <SkeletonLoader variant="text" className="w-32" />
                </div>
                <SkeletonLoader variant="circular" width={32} height={32} />
              </div>
            </div>
          ))}
        </div>

        {/* Recent Trees Table Skeleton */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b">
            <SkeletonLoader variant="text" className="w-48" />
          </div>
          <div className="divide-y divide-gray-200">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="p-6">
                <div className="flex items-center space-x-4">
                  <SkeletonLoader width={64} height={64} className="rounded-lg" />
                  <div className="flex-1 space-y-3">
                    <SkeletonLoader variant="text" className="w-1/3" />
                    <SkeletonLoader variant="text" className="w-1/4" />
                  </div>
                  <SkeletonLoader variant="text" className="w-20" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const StatCard = ({ icon: Icon, title, value, description, color }: any) => (
    <div className={`bg-white p-6 rounded-xl shadow-lg border-l-4 ${color}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
        <Icon className="h-8 w-8 text-gray-400" />
      </div>
    </div>
  );

  return (
    <ErrorBoundary>
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={TreePine}
            title="Total de Árboles"
            value={stats.totalTrees}
            description="Participaciones totales"
            color="border-primary"
          />
          <StatCard
            icon={Clock}
            title="Pendientes"
            value={stats.pendingTrees}
            description="Necesitan revisión"
            color="border-yellow-500"
          />
          <StatCard
            icon={Award}
            title="Aprobados"
            value={stats.approvedTrees}
            description="Árboles publicados"
            color="border-green-500"
          />
          <StatCard
            icon={Users}
            title="Votos Totales"
            value={stats.totalVotes}
            description="Interacciones de usuarios"
            color="border-blue-500"
          />
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-lg font-bold text-gray-800">
              Participaciones Recientes
            </h2>
            <Link
              to="/admin/trees"
              className="text-primary hover:text-primary-dark flex items-center text-sm font-medium"
            >
              Ver todos
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[1000px]">
              <div className="grid grid-cols-[100px_200px_200px_120px_120px] gap-4 px-6 py-3 bg-gray-50 border-b">
                <div className="text-xs font-medium text-gray-500 uppercase">Imagen</div>
                <div className="text-xs font-medium text-gray-500 uppercase">Árbol</div>
                <div className="text-xs font-medium text-gray-500 uppercase">Creador</div>
                <div className="text-xs font-medium text-gray-500 uppercase">Estado</div>
                <div className="text-xs font-medium text-gray-500 uppercase">Votos</div>
              </div>

              {stats.recentTrees.map((tree: any, index) => {
                const { label, classes } = getStatusLabel(tree.status);
                return (
                  <div 
                    key={tree.id}
                    className="grid grid-cols-[100px_200px_200px_120px_120px] gap-4 px-6 py-4 items-center hover:bg-gray-50 border-b"
                  >
                    <div>
                      <ImageWithFallback
                        src={tree.imageUrl}
                        alt={tree.name}
                        className="h-10 w-10 rounded-lg object-cover"
                        width={40}
                        height={40}
                        priority={index < 2}
                      />
                    </div>
                    <div className="truncate font-medium text-gray-900">{tree.name}</div>
                    <div className="truncate text-gray-600">{tree.creator}</div>
                    <div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${classes}`}>
                        {label}
                      </span>
                    </div>
                    <div className="text-gray-600">{tree.votes || 0}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}